/**
 * Server settings › Channels, Reorder mode (2026-10-29): sorting channels
 * and categories without a drag, for touch screens (the sidebar's drag and
 * drop is for a mouse or a pen) and the keyboard. Each channel moves up or
 * down inside its own group (text before voice, Discord's rule) and to
 * another category, or out of every category; each category moves up or
 * down as a block. Every move is one drop (`ChannelDrop`), sent and shown
 * as the sidebar's (`commitChannelDrop`: `PATCH /guilds/{id}/channels`,
 * put back if refused).
 */
import { ChannelType, isVoiceChannelType, type Channel, type Snowflake } from '@neosocial/protocol';
import { useEffect, useRef } from 'react';
import { useT } from '../i18n';
import { commitChannelDrop } from '../hub/sidebarDnd';
import type { ChannelDrop } from '../store/reorder';
import { channelIcon, type CategoryNode } from '../store/selectors';
import { Icon, IconBtn } from '../ui/kit';
import { openMenu, type MenuItem } from '../ui/overlay';

type Act = 'up' | 'down';

/**
 * The drops for one item's buttons: `up` and `down` (null at its group's
 * end: nothing to swap with), and `into(parentId)` for a channel's new
 * category (it lands at the end of its group there).
 */
export function reorderMoves(
  tree: readonly CategoryNode[],
  item: Channel,
): { up: ChannelDrop | null; down: ChannelDrop | null } {
  if (item.type === ChannelType.GUILD_CATEGORY) {
    const cats = tree.flatMap((n) => (n.category ? [n.category] : []));
    const i = cats.findIndex((c) => c.id === item.id);
    return {
      up: i > 0 ? { id: item.id, parentId: null, index: i - 1 } : null,
      down: i >= 0 && i < cats.length - 1 ? { id: item.id, parentId: null, index: i + 1 } : null,
    };
  }
  const parentId = item.parent_id ?? null;
  const shown = tree.find((n) => (n.category?.id ?? null) === parentId)?.channels ?? [];
  const at = shown.findIndex((c) => c.id === item.id);
  const voice = isVoiceChannelType(item.type);
  const group = shown.filter((c) => isVoiceChannelType(c.type) === voice);
  const g = group.findIndex((c) => c.id === item.id);
  return {
    up: g > 0 ? { id: item.id, parentId, index: at - 1 } : null,
    down: g >= 0 && g < group.length - 1 ? { id: item.id, parentId, index: at + 1 } : null,
  };
}

/** A channel moved to `parentId` (a category, or null: out of every category), at the end of its group there. */
export function intoCategory(
  tree: readonly CategoryNode[],
  item: Channel,
  parentId: Snowflake | null,
): ChannelDrop | null {
  if ((item.parent_id ?? null) === parentId) return null;
  const n = tree.find((x) => (x.category?.id ?? null) === parentId)?.channels.length ?? 0;
  return { id: item.id, parentId, index: n };
}

export function ChannelReorder({ guildId, tree }: { guildId: Snowflake; tree: CategoryNode[] }) {
  const { t } = useT();
  const items = tree.flatMap((n) => [...(n.category ? [n.category] : []), ...n.channels]);
  const cats = tree.flatMap((n) => (n.category ? [n.category] : []));
  const list = useRef<HTMLUListElement>(null);
  // the button pressed keeps the focus once its row has moved (or its twin,
  // when the move took the row to its group's end)
  const last = useRef<{ id: Snowflake; act: Act } | null>(null);
  useEffect(() => {
    const l = last.current;
    const box = list.current;
    if (!l || !box) return;
    const row = box.querySelector<HTMLElement>(`[data-reorder="${l.id}"]`);
    const btn =
      row?.querySelector<HTMLButtonElement>(`[data-act="${l.act}"]:not(:disabled)`) ??
      row?.querySelector<HTMLButtonElement>(`[data-act]:not(:disabled)`);
    if (btn && document.activeElement !== btn) btn.focus();
  });
  const go = (drop: ChannelDrop | null, focus?: { id: Snowflake; act: Act }) => {
    if (!drop) return;
    last.current = focus ?? null;
    void commitChannelDrop(guildId, drop);
  };
  return (
    <>
      <p className="ns-reorder-hint">{t('um.drag.reorderHint')}</p>
      <ul ref={list}>
        {items.map((c) => {
          const cat = c.type === ChannelType.GUILD_CATEGORY;
          const m = reorderMoves(tree, c);
          return (
            <li
              key={c.id}
              className={'ns-reorder-row' + (cat ? ' cat' : '')}
              role="group"
              aria-label={c.name ?? ''}
              data-reorder={c.id}
            >
              <Icon name={channelIcon(c.type)} />
              <span className="ns-reorder-n" dir="auto">
                {c.name}
              </span>
              <IconBtn
                icon="chev-up"
                label={t('um.drag.moveUp')}
                data-act="up"
                disabled={!m.up}
                onClick={() => go(m.up, { id: c.id, act: 'up' })}
              />
              <IconBtn
                icon="chev-down"
                label={t('um.drag.moveDown')}
                data-act="down"
                disabled={!m.down}
                onClick={() => go(m.down, { id: c.id, act: 'down' })}
              />
              {!cat ? (
                <IconBtn
                  icon="list"
                  label={t('um.drag.moveToCategory')}
                  aria-haspopup="menu"
                  onClick={(e) => {
                    const here = c.parent_id ?? null;
                    const pick = (id: Snowflake | null): MenuItem => ({
                      label: id
                        ? (cats.find((x) => x.id === id)?.name ?? '')
                        : t('ns.admin.noCategory'),
                      checked: here === id,
                      run: () => go(intoCategory(tree, c, id)),
                    });
                    openMenu(e.currentTarget, [
                      { header: t('um.drag.moveToCategory') },
                      pick(null),
                      ...cats.map((x) => pick(x.id)),
                    ]);
                  }}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </>
  );
}
